/**
 * Shared column contract for the Order Hub importer.
 *
 * Read by the frontend wizard (labels, aliases, examples, cell validation) and
 * by the backend's round-trip test, which asserts these headers against the
 * export's. One definition, so the export → import round trip cannot drift.
 */

import { ORDER_STATUS_VALUES, ORDER_SUB_TYPE_VALUES } from './schemas/order.js';
import {
  ORDER_LINE_UNIT_VALUES,
  type OrderLineUnit,
} from './schemas/order-line.js';

/** How a raw cell string becomes a payload value. */
export type OrderImportTransform =
  /** Trimmed string, passed through. */
  | 'text'
  /** Trimmed string validated against `enumValues`. */
  | 'enum'
  /** `YYYY-MM-DD` (PG `date`, never a timestamp). */
  | 'date'
  /** Numeric cell. */
  | 'number'
  /** One tag per column, collected into `tags[]`. */
  | 'tag'
  /** `"TYPE:value"` pairs, `; `-separated — the first pair is the match key. */
  | 'productCode'
  /** `; `-separated list of ids / emails. */
  | 'idList';

/** Where the value lands: the order group, or the order line the row describes. */
export type OrderImportScope = 'order' | 'line';

export interface IOrderImportColumn {
  /** Export-identical header text. Also the importer's `label`. */
  readonly header: string;
  /** Dot path on the bulk-import payload, relative to `scope`. */
  readonly field: string;
  readonly scope: OrderImportScope;
  readonly transform: OrderImportTransform;
  /** Blocking when the cell is empty. */
  readonly required?: boolean;
  /** Extra accepted header spellings for the frontend's column matcher. */
  readonly aliases?: readonly string[];
  /** Allowed values — only set for `transform: 'enum'`. */
  readonly enumValues?: readonly string[];
  /** Filled cell for the template's example row and the review step's `Ex.` row. */
  readonly example: string;
  /**
   * The header repeats with a 1-based index (`Custom Tag 1`, `Custom Tag 2`, …).
   * `header` carries the index-1 spelling; `headerPattern` matches any index.
   */
  readonly headerPattern?: RegExp;
  /** Short note for the template guide / importer tooltip. */
  readonly note?: string;
  /**
   * True when the export does not emit this header. Template-only columns are
   * real order fields that simply have no export column.
   */
  readonly importerOnly?: boolean;
}

/**
 * Importable columns, in export header order, with the importer-only columns
 * appended. `Order Type` is deliberately absent — it is derived from
 * buyer/supplier versus the importing company, never read from the file.
 */
export const ORDER_IMPORT_COLUMNS: readonly IOrderImportColumn[] = [
  {
    header: 'PO Number',
    field: 'orderNumber',
    scope: 'order',
    transform: 'text',
    required: true,
    aliases: [
      'Order Number',
      'Purchase Order Number',
      // The Order Tracing PO and SO templates, which clients reuse for the hub.
      'PO Number (MANDATORY)',
      'SO Number',
      'Sales Order Number',
    ],
    example: 'PO-1001',
    note: 'Groups the file into orders and decides create versus update.',
  },
  {
    header: 'Counterparty PO Reference',
    field: 'counterpartyOrderNumberRef',
    scope: 'order',
    transform: 'text',
    aliases: ['Counterparty PO', 'Customer PO Number', 'Supplier PO Number'],
    example: 'CP-2001',
  },
  {
    header: 'Status',
    field: 'status',
    scope: 'order',
    transform: 'enum',
    aliases: ['Order Status'],
    enumValues: ORDER_STATUS_VALUES,
    example: 'IN_PROGRESS',
  },
  {
    header: 'Order Date',
    field: 'orderDate',
    scope: 'order',
    transform: 'date',
    aliases: ['PO Date', 'Purchase Order Date', 'Order Date (optional)'],
    example: '2026-01-15',
  },
  {
    header: 'Delivery Date',
    field: 'deliveryDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Requested Delivery Date', 'Delivery Date (optional)'],
    example: '2026-03-01',
  },
  {
    header: 'Agreed Delivery Date',
    field: 'agreedDeliveryDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Confirmed Delivery Date', 'Agreed Delivery Date (optional)'],
    example: '2026-03-05',
  },
  {
    header: 'Shipping Date',
    field: 'shippingDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Ship Date'],
    example: '2026-02-20',
  },
  {
    header: 'Ex-Factory Date',
    field: 'expectedShippingDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Ex Factory Date', 'Expected Shipping Date', 'ExFactory'],
    example: '2026-02-14',
    note: "Stored as the order's expected shipping date.",
  },
  {
    header: 'Shipment ID / Reference',
    field: 'shipmentReference',
    scope: 'order',
    transform: 'text',
    aliases: ['Shipment ID', 'Shipment Reference'],
    example: 'SHP-77',
  },
  {
    header: 'Custom Tag 1',
    field: 'tags',
    scope: 'order',
    transform: 'tag',
    aliases: [
      'Tag',
      'Tags',
      'Custom Tag',
      'Label',
      'Custom Tags',
      'Custom Tags (Optional)',
    ],
    headerPattern: /^Custom Tag (\d+)$/i,
    example: 'priority',
    note: 'One tag per column. Tags are stored comma-joined, so avoid commas.',
  },
  {
    header: 'Supplier',
    field: 'supplier.officialName',
    scope: 'order',
    transform: 'text',
    aliases: ['Supplier Name', 'Supplier Company', 'Seller'],
    example: 'ACME Textiles',
    note: 'Only used when the supplier internal company code is blank.',
  },
  {
    header: 'Supplier Internal Company Code',
    field: 'supplier.internalCompanyCode',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Supplier Code',
      'Supplier ID',
      'SUPPLIER_INTERNAL_COMPANY_CODE',
      'Supplier Company ID',
      'Supplier Company ID (MANDATORY)',
    ],
    example: 'SUP-001',
    note: 'The code your company gave the supplier in Retraced. Wins over the name when both are filled.',
  },
  {
    header: 'Supplier Facility Process',
    field: 'supplierFacilityProcess',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Supplier Process',
      'SUPPLIER_FACILITY_PROCESS',
      'Supplier Facility Process ID',
      'Supplier Facility Process ID (MANDATORY)',
      'Facility Process ID',
    ],
    example: 'SEWING',
  },
  {
    header: 'Buyer',
    field: 'buyer.officialName',
    scope: 'order',
    transform: 'text',
    aliases: ['Buyer Name', 'Buying Company', 'Customer'],
    example: 'Retraced Fashion',
    note: 'Only used when the buyer internal company code is blank. Leave both blank when the buyer is your own company.',
  },
  {
    header: 'Buyer Internal Company Code',
    field: 'buyer.internalCompanyCode',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Buyer Code',
      'Buyer ID',
      'BUYER_INTERNAL_COMPANY_CODE',
      'Buying Company ID',
      'Buyer Company ID',
    ],
    example: 'BUY-001',
    note: 'The code your company gave the buyer in Retraced. Wins over the name when both are filled.',
  },
  {
    header: 'Buying Company Facility Process',
    field: 'buyerFacilityProcess',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Buyer Facility Process',
      'Buyer Process',
      'BUYING_COMPANY_FACILITY_PROCESS',
      'Buying Company Facility Process ID',
      'Buying Company Facility Process ID (MANDATORY)',
    ],
    example: 'DESIGN',
  },
  {
    header: 'Receiver',
    field: 'receiver.officialName',
    scope: 'order',
    transform: 'text',
    aliases: ['Receiver Name', 'Ship To', 'Receiver Company ID', 'Receiver ID'],
    example: 'Retraced Fashion',
    note: 'Pair with a receiver facility process, or leave both blank.',
  },
  {
    header: 'Vendor',
    field: 'vendor.officialName',
    scope: 'order',
    transform: 'text',
    aliases: ['Vendor Name', 'Vendor Company'],
    example: 'Vendor Co',
  },
  {
    header: 'Factory',
    field: 'factory.officialName',
    scope: 'order',
    transform: 'text',
    aliases: ['Factory Name', 'Manufacturer'],
    example: 'Factory Co',
  },
  {
    header: 'Order Line ID',
    field: 'orderLineId',
    scope: 'line',
    transform: 'text',
    aliases: ['Line ID'],
    example: '',
    note: 'Filled by the export. Leave blank for a new line.',
  },
  {
    header: 'Order Line Number',
    field: 'orderLineNumber',
    scope: 'line',
    transform: 'text',
    aliases: ['Line Reference'],
    example: '',
    note: 'Matches an existing line when the order line id is blank.',
  },
  {
    header: 'Product ID',
    field: 'productCode',
    scope: 'line',
    transform: 'productCode',
    aliases: [
      'Product Code',
      'Style Code',
      'Article Number',
      'SKU',
      'Material Number',
      'Product Code (MANDATORY)',
    ],
    required: true,
    example: 'PLM_ID:AB-1',
    note: '"TYPE:value" pairs separated by "; ", or a bare value matched across every code type of your products. The first pair is matched.',
  },
  {
    header: 'Quantity',
    field: 'quantity',
    scope: 'line',
    transform: 'number',
    aliases: [
      'Order Quantity',
      'Qty',
      'Product Quantity',
      'Product Quantity (MANDATORY)',
    ],
    required: true,
    example: '100',
  },
  {
    header: 'Unit of Measure',
    field: 'unit',
    scope: 'line',
    transform: 'enum',
    aliases: ['UOM', 'Unit', 'UOM (MANDATORY)'],
    enumValues: ORDER_LINE_UNIT_VALUES,
    required: true,
    example: 'PIECES',
  },

  // ---------------------------------------------------------------------------
  // Importer-only — real order fields the export has no column for.
  // ---------------------------------------------------------------------------
  {
    header: 'Order Sub Type',
    field: 'orderSubType',
    scope: 'order',
    transform: 'enum',
    aliases: ['Sub Type'],
    enumValues: ORDER_SUB_TYPE_VALUES,
    example: 'STANDARD',
    importerOnly: true,
  },
  {
    header: 'Handover Date',
    field: 'handoverDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Handover'],
    example: '2026-03-10',
    importerOnly: true,
  },
  {
    header: 'Actual Delivery Date',
    field: 'actualDeliveryDate',
    scope: 'order',
    transform: 'date',
    aliases: ['Delivered Date'],
    example: '2026-03-12',
    importerOnly: true,
  },
  {
    header: 'Receiver Facility Process',
    field: 'receiverFacilityProcess',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Receiver Process',
      'Receiver Facility Process ID',
      'Receiver Company Facility Process',
      'Receiver Company Facility Process ID',
    ],
    example: 'WAREHOUSING',
    importerOnly: true,
  },
  {
    header: 'Internal Responsible Person',
    field: 'internalResponsiblePersons',
    scope: 'order',
    transform: 'idList',
    aliases: ['Responsible Person', 'Owner', 'Internal Contact'],
    example: 'jane@retraced.com',
    note: 'Email or user id, separated by "; ".',
    importerOnly: true,
  },
  {
    header: 'Tracing Attachment Template',
    field: 'tracingAttachmentTemplateId',
    scope: 'order',
    transform: 'text',
    aliases: [
      'Tracing Template',
      'Attachment Template',
      'Document Template',
      'Tracing Template (Mandatory-Check with your client before selecting)',
    ],
    example: 'Standard tracing pack',
    note: 'Template name or id. Decides which documents tracing asks for.',
    importerOnly: true,
  },
  {
    header: 'Secondary Quantity',
    field: 'secondaryQuantity',
    scope: 'line',
    transform: 'number',
    aliases: ['Weight', 'Net Weight'],
    example: '25',
    note: 'Always a weight in kilograms.',
    importerOnly: true,
  },
];

/**
 * Export headers whose value is derived rather than stored on the order, so the
 * importer can never write them back. `EXPORT_ONLY_HEADERS` covers the purely
 * informational columns; these cover the derived ones.
 */
export const DERIVED_HEADERS: readonly string[] = [
  // Derived from buyer/supplier versus the importing company. The Order
  // Tracing PO template's spelling carries the tracing order kind ("Initial
  // order"), which is not an Order Hub field either.
  'Order Type',
  'Order Type ID',
  'Order Type ID (optional)',
  // The importer always records IMPORTER, so the file cannot set it.
  'Ingestion Source',
  // Come from the line's style, not the order.
  'Brand',
  'Season',
  'Product Name',
  'Product Level',
  'Product Type',
  // Company profile fields, not order fields.
  'Supplier Responsible Person',
  'Vendor Responsible Person',
  'Supplier Country',
  'Supplier City',
  'Supplier Address',
  // No `received_quantity` column exists on `order_lines`.
  'Received Quantity',
  'Received Quantity UOM',
  // Replaced by `Order Line ID` / `Order Line Number`; server-assigned and
  // reassigned on update, so it was never safe to match on. Kept here as a
  // defence against files produced by the earlier export.
  'Line Number',
];

/**
 * Export columns that carry no order field — metadata, ids and links the
 * importer can never write back.
 */
export const EXPORT_ONLY_IMPORT_HEADERS: readonly string[] = [
  'Created At',
  'Updated At',
  'Created / Updated By',
  'Ingestion Timestamp',
  'Supplier Retraced ID',
  'Buyer Retraced ID',
  'Tracing Sent Status',
  'Tracing Readiness Status',
  'Tracing Missing Requirements',
  'Tracing Request ID',
  'Tracing Request Link',
  'CMM Sent Status',
  'CMM Ready Status',
  'CMM Missing Requirements',
  'CMM Record ID',
  'CMM Record Link',
  'Number of Linked Documents',
  'Linked Document Types',
  'Document Origins',
  'Latest Document Updated At',
];

/**
 * Annotation columns an error report adds in front of the export layout — the
 * send-to-CMM report's `ERROR` column. Ignored so a report goes back in through
 * the importer as it is, which is the whole correction loop.
 */
export const ERROR_REPORT_HEADERS: readonly string[] = ['ERROR'];

/** Every header the column-matching step must skip outright. */
export const IMPORT_IGNORED_HEADERS: readonly string[] = [
  ...EXPORT_ONLY_IMPORT_HEADERS,
  ...DERIVED_HEADERS,
  ...ERROR_REPORT_HEADERS,
];

const IGNORED_HEADER_SET = new Set(
  IMPORT_IGNORED_HEADERS.map(h => h.toLowerCase()),
);

/** `ignoreHeader` predicate for the frontend importer. Case- and space-insensitive. */
export function isIgnoredImportHeader(header: string): boolean {
  return IGNORED_HEADER_SET.has(header.trim().toLowerCase());
}

/** Lookup by exact header, then by alias, then by dynamic header pattern. */
export function findImportColumn(
  header: string,
): IOrderImportColumn | undefined {
  const needle = header.trim().toLowerCase();
  if (!needle) return undefined;

  const byHeader = ORDER_IMPORT_COLUMNS.find(
    c => c.header.toLowerCase() === needle,
  );
  if (byHeader) return byHeader;

  const byAlias = ORDER_IMPORT_COLUMNS.find(c =>
    c.aliases?.some(a => a.toLowerCase() === needle),
  );
  if (byAlias) return byAlias;

  return ORDER_IMPORT_COLUMNS.find(c => c.headerPattern?.test(header.trim()));
}

/** Lookup by the payload path a column writes to. */
export function findImportColumnByField(
  field: string,
): IOrderImportColumn | undefined {
  return ORDER_IMPORT_COLUMNS.find(c => c.field === field);
}

/**
 * The file header a payload field came from. A party is named by its object
 * path (`supplier`) but by its name column in the file. Unknown fields pass
 * through unchanged.
 */
export function importFieldLabel(field: string): string {
  const column =
    findImportColumnByField(field) ??
    findImportColumnByField(`${field}.officialName`);
  return column ? column.header : field;
}

/**
 * The 1-based index carried by a dynamic header (`Custom Tag 3` → 3), or
 * `undefined` when the column is not a dynamic one.
 */
export function importHeaderIndex(
  column: IOrderImportColumn,
  header: string,
): number | undefined {
  const match = column.headerPattern?.exec(header.trim());
  if (!match?.[1]) return undefined;
  const index = Number(match[1]);
  return Number.isInteger(index) && index > 0 ? index : undefined;
}

/**
 * Canonicalises a date cell to `YYYY-MM-DD`, or `null` when it is not a date.
 *
 * Spreadsheet cells arrive as display strings, so a real Excel date renders in
 * the writer's locale (`15/01/2026`) rather than the ISO form the API takes.
 * Day-first is assumed for the ambiguous `x/y/z` case: the export writes ISO,
 * so anything slash-separated came from a human, and every locale that writes
 * slashes in Europe is day-first.
 */
export function normalizeImportDate(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
  if (iso) return isoOrNull(+iso[1], +iso[2], +iso[3]);

  const slash = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(value);
  if (slash) return isoOrNull(+slash[3], +slash[2], +slash[1]);

  // `2026-01-15T00:00:00.000Z` and friends.
  const timestamp = /^(\d{4})-(\d{2})-(\d{2})T/.exec(value);
  if (timestamp) return isoOrNull(+timestamp[1], +timestamp[2], +timestamp[3]);

  return null;
}

function isoOrNull(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Parses a numeric cell, tolerating the thousands and decimal separators a
 * spreadsheet produces. `null` when the cell is not a number.
 */
export function normalizeImportNumber(raw: string): number | null {
  const value = raw.trim().replace(/\s/g, '');
  if (!value) return null;

  // `1.234,56` is decimal-comma; `1,234.56` and `1,5` are handled below.
  const normalized =
    /^-?\d{1,3}(\.\d{3})+(,\d+)?$/.test(value) || /^-?\d+,\d+$/.test(value)
      ? value.replace(/\./g, '').replace(',', '.')
      : value.replace(/,/g, '');

  if (!/^-?\d*\.?\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface ImportProductCode {
  codeId: string | null;
  value: string;
}

/**
 * `"TYPE:value"` split on the **first** colon only — a code value may itself
 * contain colons, and multi-entry code types arrive as `OTHER_ID400813`.
 * Only the first `; `-separated pair is read; the export writes the rest for
 * information, and matching on more than one would be ambiguous.
 *
 * A bare value (no colon, as the Order Tracing templates write it) is a code
 * without a type; a cell with a colon must carry both halves.
 */
export function splitImportProductCode(
  raw: string | undefined,
): ImportProductCode | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  const first = trimmed.split(';')[0].trim();
  if (!first) return null;
  const separator = first.indexOf(':');
  if (separator < 0) return { codeId: null, value: first };
  if (separator === 0) return null;

  const codeId = first.slice(0, separator).trim();
  const value = first.slice(separator + 1).trim();
  if (!codeId || !value) return null;

  return { codeId, value };
}

const UNIT_SYNONYMS: Readonly<Record<string, OrderLineUnit>> = {
  L: 'LITRE',
  ML: 'MILLILITRE',
  CL: 'CENTILITRE',
  DM3: 'CUBIC_DECIMETRE',
  M3: 'CUBIC_METRE',
  CUBIC_METER: 'CUBIC_METRE',
  YD3: 'CUBIC_YARD',
  FT3: 'CUBIC_FOOT',
  CUBIC_FEET: 'CUBIC_FOOT',
  IN3: 'CUBIC_INCH',
  M: 'METRE',
  METER: 'METRE',
  DM: 'DECIMETRE',
  MM: 'MILLIMETRE',
  MILLIMETER: 'MILLIMETRE',
  CM: 'CENTIMETRE',
  CENTIMETER: 'CENTIMETRE',
  IN: 'INCH',
  YD: 'YARD',
  FT: 'FOOT',
  FEET: 'FOOT',
  G: 'GRAM',
  GRAMS: 'GRAM',
  G_M2: 'GRAM_PER_SQUARE_METER',
  GSM: 'GRAM_PER_SQUARE_METER',
  GRAMS_PER_SQUARE_METER: 'GRAM_PER_SQUARE_METER',
  G_FT2: 'GRAM_PER_SQUARE_FOOT',
  GRAMS_PER_SQUARE_FOOT: 'GRAM_PER_SQUARE_FOOT',
  G_YD2: 'GRAM_PER_SQUARE_YARD',
  GRAMS_PER_SQUARE_YARD: 'GRAM_PER_SQUARE_YARD',
  OZ_YD2: 'OUNCE_PER_SQUARE_YARD',
  MG: 'MILLIGRAM',
  KG: 'KILOGRAM',
  KILOGRAMS: 'KILOGRAM',
  T: 'METRIC_TON',
  TONNE: 'METRIC_TON',
  LB: 'POUNDS',
  LBS: 'POUNDS',
  POUND: 'POUNDS',
  OZ: 'OUNCE',
  M2: 'SQUARE_METRE',
  SQUARE_METER: 'SQUARE_METRE',
  SQM: 'SQUARE_METRE',
  DM2: 'SQUARE_DECIMETRE',
  CM2: 'SQUARE_CENTIMETRE',
  IN2: 'SQUARE_INCH',
  SQUARE_MILES: 'SQUARE_MILE',
  FT2: 'SQUARE_FEET',
  SQUARE_FOOT: 'SQUARE_FEET',
  ROD2: 'SQUARE_ROD',
  YD2: 'SQUARE_YARD',
  PC: 'PIECES',
  PCS: 'PIECES',
  PIECE: 'PIECES',
  PR: 'PAIR',
  PAIRS: 'PAIR',
  ROLLS: 'ROLL',
};

const UNIT_VALUES: ReadonlySet<string> = new Set(ORDER_LINE_UNIT_VALUES);

export function normalizeImportUnit(raw: string): OrderLineUnit | null {
  const key = raw
    .trim()
    .toUpperCase()
    .replace(/²/g, '2')
    .replace(/³/g, '3')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!key) return null;
  if (UNIT_VALUES.has(key)) return key as OrderLineUnit;
  return UNIT_SYNONYMS[key] ?? null;
}

/** `Unit of Measure` has 72 values; a tooltip listing all of them is unusable. */
const ENUM_HINT_LIMIT = 8;

function describeAllowedValues(values: readonly string[]): string {
  if (values.length <= ENUM_HINT_LIMIT)
    return `use one of: ${values.join(', ')}`;
  return `pick one of the ${values.length} allowed values from the dropdown, e.g. ${values
    .slice(0, ENUM_HINT_LIMIT)
    .join(', ')}`;
}

/**
 * Why the cell cannot be sent, or `undefined` when it can.
 *
 * The route parses the whole submit with one strict schema, so an uncoercible
 * cell rejects every order in the file with no row attribution. Catching it
 * here is what keeps the error on the row the user can fix.
 */
export function validateImportCell(
  column: IOrderImportColumn,
  raw: string,
): string | undefined {
  const value = raw.trim();
  if (!value) {
    return column.required ? 'Required' : undefined;
  }

  switch (column.transform) {
    case 'date':
      return normalizeImportDate(value)
        ? undefined
        : `"${value}" is not a date — use YYYY-MM-DD, e.g. ${column.example}`;
    case 'number': {
      const parsed = normalizeImportNumber(value);
      if (parsed === null) return `"${value}" is not a number`;
      return parsed > 0 ? undefined : `"${value}" must be greater than 0`;
    }
    case 'productCode':
      return splitImportProductCode(value)
        ? undefined
        : `"${value}" must be a product code, either the value alone or as ${column.example} — the code type is shown on the product in Retraced`;
    case 'enum': {
      const normalized =
        column.field === 'unit'
          ? normalizeImportUnit(value)
          : value.toUpperCase();
      return column.enumValues?.some(v => v === normalized)
        ? undefined
        : `"${value}" is not a valid ${column.header} — ${describeAllowedValues(
            column.enumValues ?? [],
          )}`;
    }
    default:
      return undefined;
  }
}
